import React, { useEffect, useRef, useState } from "react";
import { db } from "../../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import {
  FaTrash,
  FaCheck,
  FaTimes,
  FaBuilding,
  FaUser,
  FaCalendarAlt,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaReply,
  FaAlignLeft,
  FaRedo,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import StatusBadge from "../../components/StatusBadge";
import AdminModal from "../../components/AdminModal";
import FormField from "../../components/FormField";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import { approveBuildingDeletion, mensajeDeError, sinMensajeDelServidor, codigoDeError } from "../../utils/identityApi";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  btnPrimary,
  btnSecondary,
  inputBase,
  pageContainer,
} from "../../utils/designTokens";

const TABS = [
  { key: "todas", label: "Todas" },
  { key: "pendiente", label: "Pendientes" },
  { key: "procesando", label: "Procesando" },
  { key: "error", label: "Con error" },
  { key: "aprobada", label: "Aprobadas" },
  { key: "rechazada", label: "Rechazadas" },
];

// approveBuildingDeletion escribe el status de la solicitud: 'procesando' →
// 'aprobada' | 'rechazada' | 'error'. 'procesando' no está en statusColors:
// usa los colores de en_proceso.
const BADGES = {
  pendiente: { status: "pendiente" },
  procesando: { status: "en_proceso", label: "Procesando" },
  error: { status: "error", label: "Con error" },
  aprobada: { status: "aprobada" },
  rechazada: { status: "rechazada" },
};

/** Sin status cuenta como pendiente, igual que en el servidor. */
const estadoDe = (r) => r.status || "pendiente";

// El lock de una baja vence a los 15 minutos (LOCK_MS en functions/identity/edificios.js)
// y la función corta a los 9: una solicitud en 'procesando' desde hace más de 15
// minutos quedó trabada (la función se cortó sin marcar el error) y se puede
// reintentar; el servidor retoma desde donde quedó.
const TRABADA_MS = 15 * 60 * 1000;

const msDe = (ts) => (ts?.toMillis ? ts.toMillis() : 0);

// Registros que borra la baja (conteos de approveBuildingDeletion), en el orden de la cascada.
const CONTEOS = [
  ["building", "edificio", "edificios"],
  ["apartments", "unidad", "unidades"],
  ["admin", "vínculo de administrador", "vínculos de administrador"],
  ["userbuilding", "vínculo de residente", "vínculos de residentes"],
  ["employeebuilding", "vínculo de empleado", "vínculos de empleados"],
  ["occupancies", "tenencia", "tenencias"],
  ["expenses", "gasto", "gastos"],
  ["incidents", "incidencia", "incidencias"],
  ["payments", "pago", "pagos"],
  ["expensesapartments", "gasto por unidad", "gastos por unidad"],
  ["montlyaccount", "estado de cuenta mensual", "estados de cuenta mensuales"],
  ["amenitycharges", "cargo de amenities", "cargos de amenities"],
  ["amenity_signups", "reserva de amenities", "reservas de amenities"],
  ["apartmentcharges", "cargo puntual", "cargos puntuales"],
  ["buildingincomes", "ingreso del edificio", "ingresos del edificio"],
  ["criteriabuilding", "criterio de división", "criterios de división"],
  ["role_permissions", "permiso por rol", "permisos por rol"],
];
const CONTEOS_CONOCIDOS = new Set(CONTEOS.map(([clave]) => clave));

const fmtNum = (n) => Number(n).toLocaleString("es-UY");
const registros = (n) => `${fmtNum(n)} ${n === 1 ? "registro" : "registros"}`;

/** { total, partes } de los conteos que deja el servidor al aprobar (solo los mayores que 0). */
const resumenConteos = (conteos) => {
  if (!conteos || typeof conteos !== "object") return null;
  const partes = [];
  let total = 0;
  const sumar = (valor, singular, plural) => {
    const n = Number(valor) || 0;
    if (n <= 0) return;
    total += n;
    partes.push(`${fmtNum(n)} ${n === 1 ? singular : plural}`);
  };
  CONTEOS.forEach(([clave, singular, plural]) => sumar(conteos[clave], singular, plural));
  Object.keys(conteos)
    .filter((clave) => !CONTEOS_CONOCIDOS.has(clave))
    .forEach((clave) => sumar(conteos[clave], clave, clave));
  return total > 0 ? { total, partes } : null;
};

// Errores genéricos del SDK de Functions (red caída): su message es el código
// ("internal"), que no le dice nada al usuario.
const MENSAJE_GENERICO = {
  internal: "No pudimos confirmar el resultado (error de conexión o del servidor). Revisá el estado de la solicitud y, si hace falta, reintentá.",
  unavailable: "El servidor no está disponible. Reintentá en unos minutos.",
};

const mensajeLegible = (e) => {
  const fallback = "Error al procesar la solicitud";
  return sinMensajeDelServidor(e) ? MENSAJE_GENERICO[codigoDeError(e)] || fallback : mensajeDeError(e, fallback);
};

const SolicitudesEliminacion = ({ setSelected }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pendiente");
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");
  // Solicitudes con una llamada a approveBuildingDeletion en vuelo (id → true).
  const [enCurso, setEnCurso] = useState({});
  const enCursoRef = useRef(new Set());
  // Solicitudes resueltas desde esta vista: siguen visibles en la pestaña
  // actual (con 'procesando' y después el resultado) hasta cambiar de pestaña.
  const [fijadas, setFijadas] = useState(() => new Set());
  // Solicitud del modal abierto: al terminar una llamada, se cierra solo si sigue siendo esa.
  const modalIdRef = useRef(null);
  const [ahora, setAhora] = useState(() => Date.now());
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();

  // Listener en tiempo real para todas las solicitudes
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "building_deletion_requests"),
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => {
          const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return tb - ta;
        });
        setRequests(data);
        setLoading(false);
      },
      (err) => {
        console.error("Error escuchando solicitudes de eliminación:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Reloj para detectar bajas trabadas, solo mientras alguna está en 'procesando'.
  const hayProcesando = requests.some((r) => estadoDe(r) === "procesando");
  useEffect(() => {
    if (!hayProcesando) return undefined;
    const timer = setInterval(() => setAhora(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, [hayProcesando]);

  const estaTrabada = (r) => {
    if (estadoDe(r) !== "procesando") return false;
    const desde = msDe(r.updatedAt);
    return desde > 0 && ahora - desde > TRABADA_MS;
  };

  const cambiarTab = (key) => {
    setActiveTab(key);
    setFijadas(new Set());
  };

  const openActionModal = (request, action) => {
    modalIdRef.current = request.id;
    setSelectedRequest(request);
    setModalAction(action);
    setAdminResponse("");
    setShowModal(true);
  };

  // Cerrar no corta nada: la baja sigue en el servidor y el resultado llega igual.
  const cerrarModal = () => {
    modalIdRef.current = null;
    setShowModal(false);
  };

  const cerrarModalSi = (id) => {
    if (modalIdRef.current === id) cerrarModal();
  };

  const marcarEnCurso = (id, activo) => {
    if (activo) enCursoRef.current.add(id);
    else enCursoRef.current.delete(id);
    setEnCurso((prev) => {
      const next = { ...prev };
      if (activo) next[id] = true;
      else delete next[id];
      return next;
    });
  };

  /**
   * Aprueba o rechaza en el servidor (approveBuildingDeletion): la cascada, el
   * status de la solicitud y el audit los hace la función. El onSnapshot muestra
   * 'procesando' y después el resultado (conteos) o el error.
   */
  const resolver = async (req, decision, respuesta) => {
    if (!req || enCursoRef.current.has(req.id)) return;
    marcarEnCurso(req.id, true);
    setFijadas((prev) => new Set(prev).add(req.id));
    const nombre = req.buildingName || "Sin nombre";
    let terminarBaja = null; // borrado_parcial: ofrecer reintentar la aprobación
    try {
      const res = await approveBuildingDeletion({ solicitudId: req.id, decision, respuesta });
      if (res?.estado === "aprobada") {
        const resumen = resumenConteos(res.conteos);
        showToast(
          res.repetido
            ? `La eliminación de "${nombre}" ya estaba completa.`
            : `Edificio "${nombre}" eliminado${resumen ? ` (${registros(resumen.total)})` : ""}.`,
          "success"
        );
      } else {
        showToast(res?.repetido ? "La solicitud ya estaba rechazada." : "Solicitud de eliminación rechazada.", "success");
      }
      cerrarModalSi(req.id);
    } catch (err) {
      console.error("Error procesando solicitud:", err);
      const codigo = codigoDeError(err);
      const motivo = err?.details?.motivo || "";
      const msg = mensajeLegible(err);
      if (codigo === "failed-precondition" && motivo === "borrado_parcial") {
        terminarBaja = msg;
      } else if (codigo === "deadline-exceeded") {
        // La función sigue trabajando: el status de la solicitud dice cómo termina.
        showToast("La eliminación sigue en curso en el servidor. El estado de la solicitud se actualiza solo en esta pantalla.", "info");
      } else if (codigo === "failed-precondition" && ["en_proceso", "ya_aprobada", "ya_rechazada"].includes(motivo)) {
        showToast(msg, "info");
      } else {
        // Validación (details.paso 'validacion') o falla de la cascada (internal con
        // details.paso): la solicitud queda en 'error' y se puede reintentar o rechazar.
        showToast(msg, "error");
      }
      // invalid-argument: el modal queda abierto para corregir la respuesta.
      if (codigo !== "invalid-argument") cerrarModalSi(req.id);
    } finally {
      marcarEnCurso(req.id, false);
    }
    if (terminarBaja) {
      const ok = await confirmDialog(
        `${terminarBaja}\n\n¿Reintentar la aprobación ahora? Se terminan de borrar los datos de "${nombre}".`,
        { title: "Terminar la eliminación", confirmLabel: "Reintentar aprobación", danger: true }
      );
      // La respuesta escrita era para el rechazo: la aprobación va sin respuesta.
      if (ok) await resolver(req, "aprobar", "");
    }
  };

  const handleAction = () => {
    if (!selectedRequest) return;
    resolver(selectedRequest, modalAction, adminResponse.trim());
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "America/Montevideo",
    });
  };

  const filteredRequests = requests.filter(
    (r) => activeTab === "todas" || estadoDe(r) === activeTab || fijadas.has(r.id)
  );

  const counts = TABS.reduce((acc, tab) => {
    acc[tab.key] = tab.key === "todas" ? requests.length : requests.filter((r) => estadoDe(r) === tab.key).length;
    return acc;
  }, {});

  const procesandoSel = !!(selectedRequest && enCurso[selectedRequest.id]);
  const esReintento = !!selectedRequest && ["error", "procesando"].includes(estadoDe(selectedRequest));

  return (
    <div style={styles.container}>
      <ScreenHeader
        icon={FaTrash}
        title="Solicitudes de Eliminación"
        subtitle="Revisá y aprobá o rechazá solicitudes de eliminación de edificios"
        onBack={() => setSelected("homesuperadmin")}
      />

      {/* Tabs */}
      <div style={styles.tabsRow}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {}),
            }}
            onClick={() => cambiarTab(tab.key)}
          >
            {tab.label}
            <span
              style={{
                ...styles.tabCount,
                ...(activeTab === tab.key ? styles.tabCountActive : {}),
              }}
            >
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Lista de solicitudes */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={{ color: colors.gray600, marginTop: 12 }}>Cargando solicitudes...</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <EmptyState
          icon={FaSearch}
          title="No se encontraron solicitudes"
        />
      ) : (
        <div style={styles.cardList}>
          {filteredRequests.map((req) => {
            const estado = estadoDe(req);
            const badge = BADGES[estado] || { status: estado };
            const trabada = estaTrabada(req);
            const ocupada = !!enCurso[req.id];
            const resumen = estado === "aprobada" ? resumenConteos(req.conteos) : null;
            // Pendiente: aprobar o rechazar. Error o baja trabada: reintentar la
            // aprobación o rechazar (si ya se borró algo, el servidor no deja rechazar).
            const conAcciones = estado === "pendiente" || estado === "error" || trabada;
            return (
            <div key={req.id} style={styles.requestCard}>
              <div style={styles.cardTop}>
                <div style={styles.cardTopLeft}>
                  <div style={styles.buildingRow}>
                    <FaBuilding style={{ color: colors.primary, marginRight: 6 }} />
                    <span style={{ fontWeight: 400, fontSize: 15, color: colors.black }}>
                      {req.buildingName || "Sin nombre"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    <StatusBadge status={badge.status} label={badge.label} />
                  </div>
                </div>
                <div style={styles.cardTopRight}>
                  <span style={{ fontSize: 12, color: colors.gray400 }}>
                    <FaCalendarAlt style={{ marginRight: 4 }} />
                    {formatDate(req.createdAt)}
                  </span>
                </div>
              </div>

              <div style={styles.cardBody}>
                {/* Solicitado por */}
                <div style={styles.metaRow}>
                  <FaUser style={{ color: colors.gray400, marginRight: 6, flexShrink: 0 }} size={12} />
                  <span style={{ fontSize: 13, color: colors.gray600 }}>
                    Solicitado por: {req.requestedBy || "Desconocido"}
                  </span>
                </div>

                {/* Motivo */}
                <div style={styles.reasonBox}>
                  <FaAlignLeft style={{ color: colors.gray400, marginRight: 8, flexShrink: 0, marginTop: 2 }} size={12} />
                  <span style={{ fontSize: 14, color: colors.gray900, lineHeight: 1.5 }}>
                    {req.reason || "Sin motivo especificado"}
                  </span>
                </div>

                {/* Baja en curso en el servidor */}
                {estado === "procesando" && (
                  <div style={styles.processingBox}>
                    {trabada ? (
                      <FaExclamationTriangle style={styles.boxIcon} size={12} />
                    ) : (
                      <FaSpinner className="spin" style={styles.boxIcon} size={12} />
                    )}
                    <span>
                      {trabada
                        ? "La eliminación no avanza desde hace más de 15 minutos. Reintentá la aprobación: retoma desde donde quedó."
                        : "Eliminando los datos del edificio. Puede tardar unos minutos; el estado se actualiza solo."}
                    </span>
                  </div>
                )}

                {/* Error de la baja (validación o un paso de la cascada) */}
                {estado === "error" && (
                  <div style={styles.errorBox}>
                    <FaExclamationTriangle style={styles.boxIcon} size={12} />
                    <span>
                      {req.errorMensaje || "La eliminación no se pudo completar. Reintentá la aprobación o rechazá la solicitud."}
                    </span>
                  </div>
                )}

                {/* Resultado de la baja */}
                {resumen && (
                  <div style={styles.resultBox}>
                    <FaCheckCircle style={styles.boxIcon} size={12} />
                    <span>
                      {resumen.total === 1 ? "Se eliminó" : "Se eliminaron"} {registros(resumen.total)}: {resumen.partes.join(" · ")}.
                    </span>
                  </div>
                )}

                {/* Respuesta del superadmin */}
                {req.adminResponse && (
                  <div style={styles.responseBox}>
                    <FaReply style={{ color: colors.primary, marginRight: 6, flexShrink: 0, marginTop: 2 }} size={12} />
                    <div>
                      <span style={{ fontSize: 12, color: colors.gray400 }}>
                        Respuesta de {req.respondedBy || "superadmin"}:
                      </span>
                      <p style={{ fontSize: 13, color: colors.gray700, margin: "4px 0 0" }}>
                        {req.adminResponse}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {conAcciones && (
                <div style={styles.cardActions}>
                  <button
                    style={{ ...styles.approveBtn, ...(ocupada ? styles.btnDisabled : {}) }}
                    onClick={() => openActionModal(req, "aprobar")}
                    disabled={ocupada}
                  >
                    {estado === "pendiente" ? (
                      <><FaCheck style={{ marginRight: 6 }} /> Aprobar eliminación</>
                    ) : (
                      <><FaRedo style={{ marginRight: 6 }} /> Reintentar aprobación</>
                    )}
                  </button>
                  <button
                    style={{ ...styles.rejectBtn, ...(ocupada ? styles.btnDisabled : {}) }}
                    onClick={() => openActionModal(req, "rechazar")}
                    disabled={ocupada}
                  >
                    <FaTimes style={{ marginRight: 6 }} /> Rechazar
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Modal de confirmacion */}
      <AdminModal
        open={showModal}
        onClose={cerrarModal}
        title={
          modalAction === "aprobar"
            ? esReintento
              ? "Reintentar eliminación de edificio"
              : "Aprobar eliminación de edificio"
            : "Rechazar eliminación de edificio"
        }
        icon={
          modalAction === "aprobar"
            ? <FaCheckCircle style={{ color: colors.success }} />
            : <FaTimesCircle style={{ color: colors.error }} />
        }
        subtitle={selectedRequest?.buildingName || ""}
        size="narrow"
        footer={
          <>
            <button style={styles.cancelBtn} onClick={cerrarModal}>
              {procesandoSel ? "Cerrar" : "Cancelar"}
            </button>
            <button
              style={{
                ...(modalAction === "aprobar" ? styles.confirmApproveBtn : styles.confirmRejectBtn),
                ...(procesandoSel ? styles.btnDisabled : {}),
              }}
              onClick={handleAction}
              disabled={procesandoSel}
            >
              {procesandoSel
                ? "Procesando..."
                : modalAction === "aprobar"
                ? esReintento
                  ? "Reintentar eliminación"
                  : "Confirmar eliminación"
                : "Confirmar rechazo"}
            </button>
          </>
        }
      >
        {modalAction === "aprobar" && (
          <div style={{
            padding: "10px 14px",
            borderRadius: 8,
            backgroundColor: colors.errorLight,
            border: `1px solid ${colors.error}`,
            marginBottom: 12,
            fontSize: 13,
            color: colors.errorDark,
          }}>
            Esta acción eliminará permanentemente el edificio y todos sus datos asociados
            (unidades, gastos, incidencias, pagos, etc.). No se puede deshacer.
          </div>
        )}
        {modalAction === "aprobar" && procesandoSel && (
          <p style={styles.modalHint}>
            La eliminación corre en el servidor y puede tardar unos minutos. Podés cerrar esta
            ventana: el proceso sigue y el estado se actualiza en la lista.
          </p>
        )}
        <FormField label="Respuesta (opcional)" htmlFor="solicitud-baja-respuesta">
          <textarea
            id="solicitud-baja-respuesta"
            style={styles.textarea}
            rows={4}
            maxLength={1000}
            placeholder="Escribí un comentario o motivo..."
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            disabled={procesandoSel}
          />
        </FormField>
      </AdminModal>
    </div>
  );
};

// Estilos migrados a design tokens
const styles = {
  container: {
    ...pageContainer,
  },
  tabsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: spacing[1],
    marginBottom: spacing[5],
    background: colors.gray100,
    borderRadius: radius.sm,
    padding: spacing[1],
    border: `1px solid ${colors.gray200}`,
  },
  tab: {
    flex: 1,
    padding: `${spacing[2]}px ${spacing[4]}px`,
    border: "none",
    borderRadius: 6,
    background: "transparent",
    cursor: "pointer",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing[2],
    fontFamily: "inherit",
    transition: `all ${transitions.normal}`,
  },
  tabActive: {
    background: colors.primary,
    color: colors.white,
    boxShadow: "0 2px 4px rgba(0, 128, 255, 0.3)",
  },
  tabCount: {
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    background: "rgba(0,0,0,0.1)",
    color: "inherit",
    borderRadius: radius.full,
    padding: "2px 8px",
  },
  tabCountActive: {
    background: "rgba(255,255,255,0.25)",
    color: colors.white,
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  // Tarjeta de solicitud — sin borde, solo sombra
  requestCard: {
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: `${spacing[4]}px ${spacing[5]}px 0`,
  },
  cardTopLeft: {},
  cardTopRight: {
    display: "flex",
    alignItems: "center",
  },
  buildingRow: {
    display: "flex",
    alignItems: "center",
  },
  cardBody: {
    padding: `${spacing[3]}px ${spacing[5]}px`,
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: spacing[2],
  },
  reasonBox: {
    display: "flex",
    alignItems: "flex-start",
    background: colors.gray50,
    padding: "10px 14px",
    borderRadius: radius.sm,
    marginTop: spacing[1],
  },
  responseBox: {
    display: "flex",
    alignItems: "flex-start",
    background: colors.primaryLighter,
    padding: "10px 14px",
    borderRadius: radius.sm,
    marginTop: spacing[2],
  },
  // Estado de la baja en el servidor: en curso, error y resultado
  processingBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    background: colors.infoLight,
    color: colors.infoDark,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    borderRadius: radius.sm,
    marginTop: spacing[2],
    fontSize: typography.sizes.base,
    lineHeight: 1.5,
  },
  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    background: colors.errorLight,
    color: colors.errorDark,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    borderRadius: radius.sm,
    marginTop: spacing[2],
    fontSize: typography.sizes.base,
    lineHeight: 1.5,
  },
  resultBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    background: colors.successLight,
    color: colors.successDark,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    borderRadius: radius.sm,
    marginTop: spacing[2],
    fontSize: typography.sizes.base,
    lineHeight: 1.5,
  },
  boxIcon: {
    flexShrink: 0,
    marginTop: spacing[1],
  },
  modalHint: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    lineHeight: 1.5,
    margin: `0 0 ${spacing[3]}px`,
  },
  cardActions: {
    display: "flex",
    gap: 10,
    padding: `0 ${spacing[5]}px ${spacing[4]}px`,
  },
  approveBtn: {
    ...btnPrimary,
    background: colors.success,
    padding: "8px 20px",
  },
  rejectBtn: {
    ...btnPrimary,
    background: colors.error,
    padding: "8px 20px",
  },
  // Botones mientras hay una llamada en vuelo para esa solicitud
  btnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  textarea: {
    ...inputBase,
    resize: "vertical",
    marginTop: spacing[2],
    boxSizing: "border-box",
    padding: "10px 14px",
  },
  cancelBtn: {
    ...btnSecondary,
    padding: "8px 20px",
  },
  confirmApproveBtn: {
    ...btnPrimary,
    background: colors.success,
    padding: "8px 20px",
  },
  confirmRejectBtn: {
    ...btnPrimary,
    background: colors.error,
    padding: "8px 20px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  spinner: {
    width: 36,
    height: 36,
    border: `3px solid ${colors.gray200}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

export default SolicitudesEliminacion;
